import { SearchRounded } from "@mui/icons-material";
import { Box, InputAdornment, TextField } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useEffect, useMemo, useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import ExportButton from "../../../../../../components/buttons/ExportButton";
import CustomToolTip from "../../../../../../components/CustomTooltip";
import StyledDataGrid from "../../../../../../components/StyledDataGrid";
import { useFileNaming } from "../../../../../../hooks/useFileNaming";
import { ComplexEntityTypes } from "../../../../../../utils/constants";
import { getUserLastActiveTimestamp } from "../../../../../../utils/search";
import { getPrettyDate } from "../../../../../../utils/timestamp";
import { useEntities } from "../../../hooks/useEntities";
import EntityDrawer from "../../EntityDrawer/EntityDrawer";
import TextIntegrationStyle from "../../TextIntegrationStyle";
import { getOktaAppConnectedUsersRows } from "./checkRowsApp";

const AppUsersView = ({ entityData }) => {
  const { useQueryForEntitiesByType } = useEntities();
  const { trackEvent } = useAPI();
  const colors = useColors();
  const { generateBreezeFileName } = useFileNaming();
  const gridRef = useGridApiRef();
  const { data: userEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.USER,
  });

  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
  const [clickedUserEntity, setClickedUserEntity] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchString, setSearchString] = useState("");

  const getGoogleAppConnectedUsersRows = (entityData, userEntities) => {
    let rowsMap = [];
    if (
      !entityData ||
      !userEntities ||
      entityData?.google_data?.user_ids?.length <= 0
    ) {
      return rowsMap;
    }
    for (const user of entityData?.google_data?.user_ids) {
      const userEntity = userEntities?.find(
        (entity) => entity.entity_id === user
      );
      if (userEntity) {
        rowsMap.push({
          id: userEntity?.entity_id,
          userId: userEntity?.unique_id,
          entityObj: userEntity,
          source: userEntity?.sources ? userEntity?.sources : [],
        });
      }
    }
    return rowsMap;
  };

  const allAppUsers = useMemo(() => {
    const okta_users = getOktaAppConnectedUsersRows(entityData, userEntities);
    if (okta_users.length > 0) {
      return okta_users;
    } else if (entityData?.google_data?.user_ids?.length > 0) {
      return getGoogleAppConnectedUsersRows(entityData, userEntities);
    }
    return [];
  }, [entityData, userEntities]);

  useEffect(() => {
    if (searchString !== "" && userEntities) {
      const lowerCaseSearchString = searchString.toLowerCase();
      const filteredRows = allAppUsers.filter((row) =>
        row?.entityObj?.display_name
          ?.toLowerCase()
          .includes(lowerCaseSearchString)
      );
      setFilteredUsers(filteredRows);
    } else {
      setFilteredUsers(allAppUsers);
    }
  }, [allAppUsers, searchString, userEntities, entityData]);

  const onUserClick = (params) => {
    trackEvent(
      `User clicked on user ID ${params?.row?.userId}, from app connected users view ${entityData?.display_name}`
    );
    setIsUserDrawerOpen(true);
    setClickedUserEntity(params.row.entityObj);
  };

  const oktaColumnsToShow = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 0.2,
        valueGetter: (params) => params?.row?.entityObj?.display_name || "",
        renderCell: (params) => (
          <CustomToolTip title="Click to view user details">
            <Box>
              <TextIntegrationStyle
                title={""}
                value={params?.row?.entityObj?.display_name}
              />
            </Box>
          </CustomToolTip>
        ),
      },
      {
        field: "email",
        headerName: "Email",
        flex: 0.25,
        valueGetter: (params) =>
          params?.row?.entityObj?.original_unique_id || "",
        renderCell: (params) => (
          <CustomToolTip title="Click to view user details">
            <Box>
              <TextIntegrationStyle
                title={""}
                value={params?.row?.entityObj?.original_unique_id}
              />
            </Box>
          </CustomToolTip>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.15,
        valueGetter: (params) =>
          params?.row?.entityObj?.okta_data?.status || "",
        renderCell: (params) => (
          <CustomToolTip title="Click to view user details">
            <Box>
              <TextIntegrationStyle
                title={""}
                value={params?.row?.entityObj?.okta_data?.status}
              />
            </Box>
          </CustomToolTip>
        ),
      },
      {
        field: "lastActive",
        headerName: "Last Active",
        flex: 0.15,
        valueGetter: (params) => {
          const lastActive = getUserLastActiveTimestamp(params?.row?.entityObj);
          return lastActive ? getPrettyDate(lastActive) : "";
        },
        renderCell: (params) => {
          const lastActive = getUserLastActiveTimestamp(params?.row?.entityObj);
          return (
            <Box>
              <TextIntegrationStyle
                title={""}
                value={lastActive ? getPrettyDate(lastActive) : "-"}
              />
            </Box>
          );
        },
      },
      {
        field: "department",
        headerName: "Department",
        flex: 0.15,
        valueGetter: (params) =>
          params?.row?.entityObj?.hibob_data?.department ||
          params?.row?.entityObj?.bamboohr_data?.department ||
          "",
        renderCell: (params) => {
          const department =
            params?.row?.entityObj?.hibob_data?.department ||
            params?.row?.entityObj?.bamboohr_data?.department;
          return (
            <Box>
              <TextIntegrationStyle title={""} value={department || "-"} />
            </Box>
          );
        },
      },
      {
        field: "manager",
        headerName: "Manager",
        flex: 0.1,
        valueGetter: (params) =>
          params?.row?.entityObj?.hibob_data?.reports_to_name ||
          params?.row?.entityObj?.bamboohr_data?.supervisor ||
          "",
        renderCell: (params) => {
          const manager =
            params?.row?.entityObj?.hibob_data?.reports_to_name ||
            params?.row?.entityObj?.bamboohr_data?.supervisor;
          return (
            <Box>
              <TextIntegrationStyle title={""} value={manager || "-"} />
            </Box>
          );
        },
      },
    ],
    []
  );

  const googleColumnsToShow = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 0.25,
        valueGetter: (params) => params?.row?.entityObj?.display_name || "",
        renderCell: (params) => (
          <Box>
            <TextIntegrationStyle
              title={""}
              value={params?.row?.entityObj?.display_name}
            />
          </Box>
        ),
      },
      {
        field: "email",
        headerName: "Email",
        flex: 0.35,
        valueGetter: (params) =>
          params?.row?.entityObj?.original_unique_id || "",
        renderCell: (params) => (
          <CustomToolTip title="Click to view user details">
            <Box>
              <TextIntegrationStyle
                title={""}
                value={params?.row?.entityObj?.original_unique_id}
              />
            </Box>
          </CustomToolTip>
        ),
      },
      {
        field: "lastActive",
        headerName: "Last Active",
        flex: 0.2,
        valueGetter: (params) => {
          const lastActive = getUserLastActiveTimestamp(params?.row?.entityObj);
          return lastActive ? getPrettyDate(lastActive) : "";
        },
        renderCell: (params) => {
          const lastActive = getUserLastActiveTimestamp(params?.row?.entityObj);
          return (
            <Box>
              <TextIntegrationStyle
                title={""}
                value={lastActive ? getPrettyDate(lastActive) : "-"}
              />
            </Box>
          );
        },
      },
      {
        field: "department",
        headerName: "Department",
        flex: 0.15,
        valueGetter: (params) =>
          params?.row?.entityObj?.hibob_data?.department ||
          params?.row?.entityObj?.bamboohr_data?.department ||
          "",
        renderCell: (params) => {
          const department =
            params?.row?.entityObj?.hibob_data?.department ||
            params?.row?.entityObj?.bamboohr_data?.department;
          return (
            <Box>
              <TextIntegrationStyle title={""} value={department || "-"} />
            </Box>
          );
        },
      },
      {
        field: "manager",
        headerName: "Manager",
        flex: 0.05,
        valueGetter: (params) =>
          params?.row?.entityObj?.hibob_data?.reports_to_name ||
          params?.row?.entityObj?.bamboohr_data?.supervisor ||
          "",
        renderCell: (params) => {
          const manager =
            params?.row?.entityObj?.hibob_data?.reports_to_name ||
            params?.row?.entityObj?.bamboohr_data?.supervisor;
          return (
            <Box>
              <TextIntegrationStyle title={""} value={manager || "-"} />
            </Box>
          );
        },
      },
    ],
    []
  );

  const columnsToShow = useMemo(() => {
    if (entityData?.sources.includes("google")) {
      return googleColumnsToShow;
    } else if (entityData?.sources.includes("okta")) {
      return oktaColumnsToShow;
    }
    return [];
  }, [entityData, googleColumnsToShow, oktaColumnsToShow]);

  const handleTableExport = () => {
    trackEvent(
      `User clicked export button for app users - ${entityData?.display_name}`
    );
    const appName =
      entityData?.display_name?.replace(/[^a-zA-Z0-9]/g, "_") || "app";
    const csvExportOptions = {
      fileName: generateBreezeFileName(`${appName}_users`),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <TextField
          InputProps={{
            sx: {
              color: colors.contrast_text_dark,
              fontSize: "14px",
            },
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded
                  style={{
                    color: colors.text_disabled,
                    width: "16px",
                    height: "16px",
                  }}
                />
              </InputAdornment>
            ),
          }}
          placeholder="Search"
          type="text"
          value={searchString}
          onChange={(e) => setSearchString(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "8px",
            },
            "& .MuiInputBase-root": { height: "32px" },
          }}
        />
        <ExportButton
          handleExport={handleTableExport}
          disabled={!filteredUsers || filteredUsers.length === 0}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <StyledDataGrid
          columns={columnsToShow}
          rows={filteredUsers}
          onRowClick={onUserClick}
          style={{ cursor: "pointer" }}
          apiRef={gridRef}
        />
      </Box>

      <EntityDrawer
        openDrawer={isUserDrawerOpen}
        selectedEntity={clickedUserEntity}
        onClose={() => setIsUserDrawerOpen(false)}
      />
    </>
  );
};
export default AppUsersView;
